<template>
  <div>
    <div
      v-for="(entry, listIndex) in localData"
      :key="entry.index"
      class="border-bottom no-gutters"
    >
      <div class="row py-3 collapse-list-row" :class="{ collapsed: !isOpen(entry) }" @click="toggle(entry)">
        <div class="col-auto">
          <text-block
            :content-id="getIdPrefix(entry.index) + 'Title'"
            :index="listIndex"
            v-on:save="(e) => save(e, listIndex)"
            :content="entry.title"
          />
        </div>
        <div class="col">
          <Button v-if="isContentManager()" icon="pi pi-trash" text rounded @click.stop="deleteRow(entry, listIndex)"/>
        </div>
        <div class="col-1 ms-auto text-end">
          <i class="pi pi-chevron-down collapse-list-chevron closed"/>
          <i class="pi pi-chevron-up collapse-list-chevron opened"/>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <div v-show="isOpen(entry)" role="tabpanel">
            <text-block
              class="px-3 pb-3"
              :id="getIdPrefix(entry.index) + 'Text'"
              :index="listIndex"
              v-on:save="(e) => save(e, listIndex)"
              :content="entry.content"
            />
          </div>
        </div>
      </div>
    </div>
    <Button v-if="isContentManager()" icon="pi pi-plus" class="mt-3" @click="addRow"/>
  </div>
</template>

<script setup>

import { usePageContent } from "@/mixins/pageContentMixin2";
import { ref, watch } from "vue";
import {injectAxios, injectAuth} from "@/util/inject";
import { useI18n } from "vue-i18n";
import TextBlock from "./TextBlock.vue";

const props = defineProps({
  list: { type: Array, default: () => [] },
  page: { type: String, default: "" },
  contentId: { type: String, default: "" },
});

const { savePageContentBatch, deletePageContentBatch } = usePageContent();
const { t, locale } = useI18n();
const auth = injectAuth();
const axios = injectAxios();
const idPrefix = ref(props.contentId);
const localData = ref([...props.list]);
const openId = ref(null);

watch(() => props.list, (val) => { localData.value = val ? [...val] : []; }, { immediate: true });
watch(() => props.contentId, (val) => { idPrefix.value = val || ""; }, { immediate: true });

const getIdPrefix = (index) => {
  return idPrefix.value + "Accordion" + index;
};
const isOpen = (entry) => openId.value === getIdPrefix(entry.index);
const toggle = (entry) => {
  const id = getIdPrefix(entry.index);
  openId.value = openId.value === id ? null : id;
};
const isContentManager = () => {
  return auth.hasRole("CONTENT_MANAGER");
};
const addRow = () => {
  const index = localData.value[localData.value.length - 1].index;
  localData.value.push({
    index: index + 1,
    title: '<div class="text16-blue bold">' + t("TITLE") + "</div>",
    content: t("CONTENT"),
    needsPersisting: true,
  });
};
const deleteRow = async (row, index) => {
  if (row.needsPersisting) {
    localData.value.splice(index, 1);
    return;
  }
  const id = getIdPrefix(row.index);
  const title = { page: props.page, lang: locale.value, element: id + "Title" };
  const content = { page: props.page, lang: locale.value, element: id + "Text" };
  if (await deletePageContentBatch(axios, [title, content])) {
    localData.value.splice(index, 1);
  }
};

const save = (elem, listIndex) => {
  const idx = elem.index === undefined ? listIndex : elem.index;
  if (idx === undefined || !localData.value[idx]) return;
  if (localData.value[idx].needsPersisting) {
    let title;
    let content;
    if (elem.element.includes("Title")) {
      title = { page: props.page, lang: locale.value, element: elem.element, value: elem.value };
      content = { page: props.page, lang: locale.value, element: elem.element.replace("Title", "Text"), value: localData.value[idx].content };
    } else {
      title = { page: props.page, lang: locale.value, element: elem.element.replace("Text", "Title"), value: localData.value[idx].title };
      content = { page: props.page, lang: locale.value, element: elem.element, value: elem.value };
    }
    localData.value[idx].needsPersisting = false;
    savePageContentBatch(axios, [title, content]);
  } else {
    const title = { page: props.page, lang: locale.value, element: elem.element.includes("Title") ? elem.element : elem.element.replace("Text", "Title"), value: elem.element.includes("Title") ? elem.value : localData.value[idx].title };
    const content = { page: props.page, lang: locale.value, element: elem.element.includes("Text") ? elem.element : elem.element.replace("Title", "Text"), value: elem.element.includes("Text") ? elem.value : localData.value[idx].content };
    savePageContentBatch(axios, [title, content]);
  }
};
</script>

<style scoped lang="scss">
.collapse-list-row { cursor: pointer; }
.collapse-list-chevron { color: blue; }
.collapse-list-row:not(.collapsed) .collapse-list-chevron.closed,
.collapse-list-row.collapsed .collapse-list-chevron.opened { display: none; }
.collapse-list-row.collapsed .collapse-list-chevron.closed,
.collapse-list-row:not(.collapsed) .collapse-list-chevron.opened { display: inline; }
</style>
